/**
 * @module NotificationPreferencesForm
 * @package @geenius/notifications-react-css
 * @description Renders the shared preference controls for the React vanilla
 * CSS variant.
 */

import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  getNotificationChannelLabel,
  getNotificationTypeLabel,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import type { JSX } from 'react'
import { cx } from '../lib/cx'
import { useNotificationCopy } from '../lib/notificationCopy'

/**
 * Async or sync handler used by the preferences form.
 */
export type NotificationPreferencesFormUpdate = (
  channels?: Record<NotificationChannel, boolean>,
  types?: Record<NotificationType, boolean>,
  quietHours?: { start: string; end: string },
) => void | Promise<void>

/**
 * Props for the React CSS preferences form.
 */
export interface NotificationPreferencesFormProps {
  preferences: NotificationPreferences
  onUpdate: NotificationPreferencesFormUpdate
  className?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']

/**
 * Renders the notification preferences form.
 *
 * @param props Preference data and update handler.
 * @returns The preference form markup.
 */
export function NotificationPreferencesForm({
  preferences,
  onUpdate,
  className,
}: NotificationPreferencesFormProps): JSX.Element {
  const { t } = useNotificationCopy()
  const quietHours = preferences.quietHours ?? { start: '22:00', end: '07:00' }

  return (
    <form
      className={cx('notifications__preferences', className)}
      aria-label={t('notifications.preferences.formAria')}
    >
      <div className={cx('notifications__preferences-section')}>
        <h3 className={cx('notifications__preferences-title')}>
          {t('notifications.preferences.sections.channels')}
        </h3>
        <div className={cx('notifications__preferences-list')}>
          {CHANNELS.map((channel) => {
            const channelLabel = getNotificationChannelLabel(channel, t)

            return (
              <div key={channel} className={cx('notifications__preferences-row')}>
              <span className={cx('notifications__preferences-label')}>
                  {channelLabel}
              </span>
              <button
                type="button"
                className={cx(
                  'notifications__preferences-toggle',
                  preferences.channels[channel]
                    ? 'notifications__preferences-toggle--on'
                    : 'notifications__preferences-toggle--off',
                )}
                aria-pressed={preferences.channels[channel]}
                aria-label={t('notifications.preferences.toggleChannelAria', {
                  channel: channelLabel,
                })}
                onClick={() =>
                  void onUpdate({
                    ...preferences.channels,
                    [channel]: !preferences.channels[channel],
                  })
                }
              >
                <span className={cx('notifications__preferences-toggle-knob')} />
              </button>
              </div>
            )
          })}
        </div>
      </div>
      <div className={cx('notifications__preferences-section')}>
        <h3 className={cx('notifications__preferences-title')}>
          {t('notifications.preferences.sections.types')}
        </h3>
        <div className={cx('notifications__preferences-list')}>
          {NOTIFICATION_TYPES.map((type) => {
            const typeLabel = getNotificationTypeLabel(type, t)

            return (
              <div key={type} className={cx('notifications__preferences-row')}>
              <span className={cx('notifications__preferences-label')}>
                  {TYPE_CONFIG[type].icon} {typeLabel}
              </span>
              <button
                type="button"
                className={cx(
                  'notifications__preferences-toggle',
                  preferences.types[type]
                    ? 'notifications__preferences-toggle--on'
                    : 'notifications__preferences-toggle--off',
                )}
                aria-pressed={preferences.types[type]}
                aria-label={t('notifications.preferences.toggleTypeAria', {
                  type: typeLabel,
                })}
                onClick={() =>
                  void onUpdate(undefined, {
                    ...preferences.types,
                    [type]: !preferences.types[type],
                  })
                }
              >
                <span className={cx('notifications__preferences-toggle-knob')} />
              </button>
              </div>
            )
          })}
        </div>
      </div>
      <div className={cx('notifications__preferences-section')}>
        <h3 className={cx('notifications__preferences-title')}>
          {t('notifications.preferences.sections.quietHours')}
        </h3>
        <div className={cx('notifications__preferences-quiet')}>
          <label
            className={cx('notifications__preferences-field')}
            htmlFor="notifications-css-quiet-start"
          >
            {t('notifications.preferences.fields.start')}
            <input
              id="notifications-css-quiet-start"
              className={cx('notifications__preferences-time-input')}
              type="time"
              value={quietHours.start}
              onChange={(event) =>
                void onUpdate(undefined, undefined, {
                  start: event.target.value,
                  end: quietHours.end,
                })
              }
            />
          </label>
          <label
            className={cx('notifications__preferences-field')}
            htmlFor="notifications-css-quiet-end"
          >
            {t('notifications.preferences.fields.end')}
            <input
              id="notifications-css-quiet-end"
              className={cx('notifications__preferences-time-input')}
              type="time"
              value={quietHours.end}
              onChange={(event) =>
                void onUpdate(undefined, undefined, {
                  start: quietHours.start,
                  end: event.target.value,
                })
              }
            />
          </label>
        </div>
      </div>
    </form>
  )
}
